import { createRouter, createWebHistory } from "vue-router";
import MainLayout from "../layouts/MainLayout";
import {
  inspectionRequests,
  inspectionRequestsV2,
} from "./inspection-requests";
import { getRefreshToken, isTokenExpired } from "@/utils/auth";
import { detailedInspectionRequestsRoutes } from "./detailed-inspection-requests";
import { initialEvaluationRoutes } from "./initial-evaluation";
import { detailedEvaluationRoutes } from "./detailed-evaluation";
import { REQUIRED_ROLES } from "./rquired-roles";
import { getAuthedRoute } from "@/store/modules/user";
import { reportsRoutes } from "./reports";
import store from "@/store";
import { handleMobileAppAuth, setupMobileAppListeners } from "@/utils/mobile-auth";

// Handle mobile app authentication before router initialization
handleMobileAppAuth();

// Setup mobile app event listeners
setupMobileAppListeners();

// get permisstion fom src/utils/allPermissionsEnum.js
const routes = [
  {
    path: "/login",
    name: "login",
    component: () => import("../views/auth/LoginView.vue"),
  },
  {
    path: "/user-operation",
    name: "user-operation",
    component: () => import("../views/auth/user-operation/index.vue"),
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/forget-password",
    name: "forget-password",
    component: () => import("../views/auth/forget-password.vue"),
    meta: { requiresAuth: false },
  },
  {
    path: "/statistics",
    name: "statistics",
    component: () => import("../views/statistics/index.vue"),
    meta: {
      requiresAuth: true,
      roles: REQUIRED_ROLES.statistics,
    },
  },
  {
    path: "/",
    name: "root",
    redirect: "/inspection-requests",
    component: MainLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("../views/dashborad/index.vue"),
      },
      inspectionRequests,
      inspectionRequestsV2,
      detailedInspectionRequestsRoutes,
      {
        path: "/manage-initial-inspections-requests",
        name: "manage-initial-inspections-requests",
        component: () =>
          import("../views/manage-inital-inspections-requests/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["manage-initial-inspections-requests"],
        },
      },
      {
        path: "/manage-detailed-inspections-requests",
        name: "manage-detailed-inspections-requests",
        component: () =>
          import("../views/manage-detailed-inspections-requests/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["manage-detailed-inspections-requests"],
        },
      },
      initialEvaluationRoutes,
      detailedEvaluationRoutes,
      reportsRoutes,
      {
        path: "/buildings-settings",
        name: "buildings-settings",
        component: () => import("../views/buildings/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["buildings-settings"],
        },
      },
      {
        path: "/buildings-classification",
        name: "buildings-classification",
        component: () => import("../views/building-classification/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["buildings-classification"],
        },
      },
      {
        path: "/org-structures",
        name: "org-structures",
        component: () => import("../views/org-structures/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["org-structures"],
        },
      },
      {
        path: "/general-settings",
        name: "general-settings-page",
        component: () => import("../views/general-settings/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["general-settings"],
        },
      },
      {
        path: "/users",
        name: "users",
        component: () => import("../views/users/index.vue"),
        meta: {
          roles: REQUIRED_ROLES.users,
        },
      },
      {
        path: "/elements-classifications",
        name: "elements-classifications",
        component: () => import("../views/elements-classifications/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["elements-classifications"],
        },
      },
      {
        path: "/blanks",
        name: "blanks",
        component: () => import("../views/blanks/index.vue"),
        meta: {
          roles: REQUIRED_ROLES.blanks,
        },
      },
      {
        path: "/logs",
        name: "logs",
        component: () => import("../views/logs/index.vue"),
        meta: {
          roles: REQUIRED_ROLES.logs,
        },
      },
      {
        path: "/audit-trail",
        name: "audit-trail",
        component: () => import("../views/audit-trail/index.vue"),
        meta: {
          roles: REQUIRED_ROLES["audit-trail"],
        },
      },
      {
        path: "/not-authorized",
        name: "not-authorized",
        component: () => import("../views/not-authorized/index.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior: () => ({ y: 0 }),
});
export const ALLOWED_UNAUTH_ROUTES = [
  '/login',
  '/user-operation',
  '/forgot-password'
];
// Add allowed unauthenticated route names for robust matching
export const ALLOWED_UNAUTH_ROUTE_NAMES = [
  'login',
  'user-operation',
  'forget-password'
];

router.beforeEach(async (to, from, next) => {
  // Check if user has a valid access token
  const hasValidToken = store.getters['user/isAuthenticated'];
  const refreshToken = getRefreshToken();
  const hasRefreshToken = !!refreshToken;
  const tokenExpired = isTokenExpired();
  
  // Debug logging
  console.log('[Router Guard] Navigation to:', to.path);
  console.log('[Router Guard] Has valid token:', hasValidToken);
  console.log('[Router Guard] Has refresh token:', hasRefreshToken);
  console.log('[Router Guard] Token expired:', tokenExpired);
  
  // Use route name for robust unauthenticated route matching
  if (ALLOWED_UNAUTH_ROUTE_NAMES.includes(to.name)) {
    // If user is authenticated and trying to access login, redirect to their authorized route
    if (hasValidToken && to.name === "login") {
      const isMobileApp = store.getters['user/isMobileApp'];
      next(getAuthedRoute(store.getters['user/userRoles'][0], isMobileApp));
    } else {
      next();
    }
    return;
  }
  
  // For all other routes, require authentication
  if (!hasValidToken) {
    if (hasRefreshToken) {
      try {
        await store.dispatch('user/refreshToken', { redirectOnFailure: false });
        next();
        return;
      } catch (error) {
        console.error("Token refresh failed:", error);
      }
    }
  
    next(`/login?redirect=${encodeURIComponent(to.fullPath)}`);
    return;
  }
  
  // User has valid token, allow access
  next();
});


export default router;






